// frontend/src/pages/PoliticaCookies.jsx
import { useState } from "react";
import { ArrowLeft, Download } from "lucide-react";

export default function PoliticaCookies() {
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const pdfUrl = `${process.env.PUBLIC_URL}/pdfs/politica-cookies.pdf`;

  const handleDownloadPDF = () => {
    setIsGeneratingPDF(true);
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = 'politica-cookies.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setIsGeneratingPDF(false);
  };

  return (
    <>
      <style>
        {`
          @media print {
            body { margin: 0; padding: 0; background: white; }
            .no-print { display: none !important; }
            .print-content { margin: 0; padding: 0.5cm; width: 100%; }
            .print-content h1 { font-size: 18pt; margin-bottom: 10pt; }
            .print-content h2 { font-size: 14pt; margin-top: 15pt; margin-bottom: 8pt; page-break-after: avoid; }
            .print-content p { font-size: 10pt; line-height: 1.4; margin-bottom: 6pt; page-break-inside: avoid; }
            .print-content ul { margin: 0 0 8pt 0; padding-left: 20pt; }
            .print-content li { font-size: 10pt; margin-bottom: 3pt; page-break-inside: avoid; }
            .print-content .section { page-break-inside: avoid; margin-bottom: 12pt; }
            .print-content .border-b { border-bottom: 0.5pt solid #ccc; }
            @page { size: A4; margin: 1.5cm; }
          }
        `}
      </style>

      <div className="min-h-screen bg-gray-50 pt-2 pb-12">
        <div className="max-w-4xl mx-auto px-4">
          {/* Header */}
          <div className="no-print bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <img
                  src={`${process.env.PUBLIC_URL}/img/logo2.png`}
                  alt="Brechó Vizinho"
                  className="h-12 w-auto object-contain"
                />
                <div>
                  <h1 className="text-xl font-bold text-gray-800">Política de Cookies</h1>
                  <p className="text-xs text-gray-500">Versão 1.0 | 15/06/2026</p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => window.history.back()}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span className="hidden sm:inline">Voltar</span>
                </button>
                <button
                  onClick={handleDownloadPDF}
                  disabled={isGeneratingPDF}
                  className="flex items-center gap-2 px-4 py-2 bg-[#449879] text-white rounded-lg hover:bg-[#2d5f4d] transition-colors disabled:opacity-50"
                >
                  {isGeneratingPDF ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  <span className="hidden sm:inline">Baixar PDF</span>
                </button>
              </div>
            </div>
          </div>

          {/* Conteúdo */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8 print:shadow-none print:p-0 print:bg-white">
            <div className="print-content prose prose-sm max-w-none prose-headings:text-gray-800 prose-p:text-gray-600 prose-strong:text-gray-800">
              
              {/* TÍTULO */}
              <div className="text-center mb-8 print:mb-4">
                <h1 className="text-2xl font-bold text-gray-800 mb-2">POLÍTICA DE COOKIES</h1>
                <p className="text-sm text-gray-500">Versão 1.0 | Data de Vigência: 15/06/2026</p>
                <div className="w-20 h-1 bg-[#449879] mx-auto mt-3 rounded-full print:bg-gray-400" />
              </div>

              {/* INTRODUÇÃO */}
              <p className="text-gray-600 leading-relaxed mb-4">
                Esta Política de Cookies explica como o Brechó Vizinho utiliza cookies e tecnologias semelhantes para melhorar 
                a experiência dos usuários, garantir o funcionamento adequado da plataforma e coletar informações 
                estatísticas sobre sua utilização.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Ao acessar e utilizar a plataforma, o usuário poderá gerenciar suas preferências de cookies conforme descrito 
                nesta Política.
              </p>

              {/* 1. O QUE SÃO COOKIES */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">1. O QUE SÃO COOKIES</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Cookies são pequenos arquivos de texto armazenados no dispositivo do usuário quando ele acessa um site 
                  ou aplicativo.
                </p>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Esses arquivos permitem reconhecer o dispositivo, lembrar preferências e melhorar a experiência de 
                  navegação.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Os cookies não permitem acesso direto a arquivos pessoais armazenados no dispositivo do usuário.
                </p>
              </div>

              {/* 2. FINALIDADE DOS COOKIES */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">2. FINALIDADE DOS COOKIES</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  O Brechó Vizinho utiliza cookies para:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Garantir o funcionamento adequado da plataforma;</li>
                  <li>Manter sessões autenticadas;</li>
                  <li>Armazenar preferências do usuário;</li>
                  <li>Melhorar a experiência de navegação;</li>
                  <li>Analisar o desempenho da plataforma;</li>
                  <li>Identificar problemas técnicos;</li>
                  <li>Medir audiência e utilização;</li>
                  <li>Personalizar conteúdos e funcionalidades.</li>
                </ul>
              </div>

              {/* 3. TIPOS DE COOKIES UTILIZADOS */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">3. TIPOS DE COOKIES UTILIZADOS</h2>
                
                <p className="text-gray-600 leading-relaxed mt-3 font-semibold">3.1 Cookies Necessários</p>
                <p className="text-gray-600 leading-relaxed mb-2">
                  São essenciais para o funcionamento da plataforma.
                </p>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Esses cookies permitem:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-3 text-gray-600 ml-4">
                  <li>Login de usuários;</li>
                  <li>Segurança da conta;</li>
                  <li>Navegação entre páginas;</li>
                  <li>Funcionamento de funcionalidades básicas.</li>
                </ul>
                <p className="text-gray-600 leading-relaxed">
                  Sem esses cookies a plataforma pode não funcionar corretamente.
                </p>

                <p className="text-gray-600 leading-relaxed mt-4 font-semibold">3.2 Cookies Estatísticos</p>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Utilizados para entender como os usuários interagem com a plataforma.
                </p>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Permitem analisar:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-3 text-gray-600 ml-4">
                  <li>Número de acessos;</li>
                  <li>Páginas mais visitadas;</li>
                  <li>Tempo de permanência;</li>
                  <li>Comportamento de navegação;</li>
                  <li>Desempenho geral do sistema.</li>
                </ul>
                <p className="text-gray-600 leading-relaxed">
                  As informações são utilizadas de forma agregada sempre que possível.
                </p>

                <p className="text-gray-600 leading-relaxed mt-4 font-semibold">3.3 Cookies de Marketing</p>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Podem ser utilizados para:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-3 text-gray-600 ml-4">
                  <li>Medir campanhas publicitárias;</li>
                  <li>Avaliar desempenho de anúncios;</li>
                  <li>Exibir conteúdos promocionais relevantes;</li>
                  <li>Melhorar estratégias de comunicação.</li>
                </ul>
                <p className="text-gray-600 leading-relaxed">
                  Esses cookies somente serão utilizados quando permitido pela legislação aplicável e pelas preferências do 
                  usuário.
                </p>
              </div>

              {/* 4. COOKIES DE TERCEIROS */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">4. COOKIES DE TERCEIROS</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  O Brechó Vizinho poderá utilizar serviços de terceiros que utilizam cookies próprios.
                </p>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Exemplos:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Google Analytics;</li>
                  <li>Google Tag Manager;</li>
                  <li>Meta Pixel;</li>
                  <li>Ferramentas de atendimento;</li>
                  <li>Plataformas de hospedagem;</li>
                  <li>Ferramentas de monitoramento e segurança.</li>
                </ul>
                <p className="text-gray-600 leading-relaxed">
                  Cada fornecedor possui suas próprias políticas de privacidade e tratamento de dados.
                </p>
              </div>

              {/* 5. GERENCIAMENTO DE PREFERÊNCIAS */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">5. GERENCIAMENTO DE PREFERÊNCIAS</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  O usuário poderá:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Aceitar todos os cookies;</li>
                  <li>Recusar cookies não essenciais;</li>
                  <li>Configurar preferências específicas.</li>
                </ul>
                <p className="text-gray-600 leading-relaxed">
                  As preferências poderão ser alteradas a qualquer momento por meio das configurações disponibilizadas pela 
                  plataforma.
                </p>
              </div>

              {/* 6. CONTROLE PELO NAVEGADOR */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">6. CONTROLE PELO NAVEGADOR</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Além das opções fornecidas pela plataforma, o usuário pode gerenciar cookies diretamente em seu navegador.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  A desativação de determinados cookies poderá impactar algumas funcionalidades do sistema.
                </p>
              </div>

              {/* 7. RETENÇÃO DAS INFORMAÇÕES */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">7. RETENÇÃO DAS INFORMAÇÕES</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Os cookies poderão permanecer armazenados pelo período necessário para cumprir suas finalidades 
                  específicas.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  O prazo poderá variar conforme o tipo de cookie utilizado.
                </p>
              </div>

              {/* 8. ALTERAÇÕES DESTA POLÍTICA */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">8. ALTERAÇÕES DESTA POLÍTICA</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Esta Política poderá ser atualizada periodicamente para refletir alterações legais, tecnológicas ou operacionais.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  A versão mais recente estará sempre disponível na plataforma.
                </p>
              </div>

              {/* CONTATO */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">CONTATO</h2>
                <p className="text-gray-600 leading-relaxed">
                  Em caso de dúvidas sobre esta Política de Cookies, o usuário poderá entrar em contato pelos canais oficiais 
                  disponibilizados pelo Brechó Vizinho.
                </p>
              </div>

              {/* 9. DISPOSIÇÕES FINAIS */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">9. DISPOSIÇÕES FINAIS</h2>
                <p className="text-gray-600 leading-relaxed">
                  Ao continuar utilizando a plataforma após visualizar o aviso de cookies, o usuário manifesta ciência das práticas 
                  descritas nesta Política, observadas as preferências por ele selecionadas.
                </p>
              </div>

              {/* ASSINATURA FINAL */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <p className="text-sm text-gray-500">
                  Última atualização: 15/06/2026<br />
                  Versão: 1.0<br />
                  Brechó Vizinho<br />
                  E-mail: <span className="text-[#449879] font-medium">info@brechovizinho.com.br</span>
                </p>
              </div>
            </div>
          </div>

          {/* Botão voltar ao topo */}
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="no-print fixed bottom-20 right-4 bg-[#449879] text-white p-3 rounded-full shadow-lg hover:bg-[#2d5f4d] transition-colors z-40"
            aria-label="Voltar ao topo"
          >
            ↑
          </button>
        </div>
      </div>
    </>
  );
}